import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { boardColor } from '@/entities/board';
import { Button, HelpTip, Icons } from '@/shared/ui';

import styles from './CalendarFilters.module.css';
import { groupSources, knownHidden, searchSources, type SourceBoard } from './sources';

interface Props {
  boards: readonly SourceBoard[];
  workspaceNames: ReadonlyMap<string, string>;
  /** Boards switched off. Ids of boards that are gone are ignored (and dropped on the next change). */
  hiddenBoardIds: readonly string[];
  onChange: (hiddenBoardIds: string[]) => void;
}

/** "3 of 8" / "all boards" / "no boards selected" - the one wording used by every source UI. */
function useSourceSummary(total: number, shown: number): string {
  const { t } = useTranslation();
  if (shown === total) return t('calendar.sources.all');
  if (shown === 0) return t('calendar.sources.none');
  return t('calendar.sources.count', { shown, total });
}

/**
 * Which boards feed the global calendar: workspaces as groups (each a tri-state checkbox that
 * switches all its boards, with a separate expand / collapse button), boards with their colour,
 * a search over workspace and board names, and Show all / Hide all. Search and collapsing only
 * change what is DRAWN; the selection changes only through the checkboxes and the two buttons.
 */
export function CalendarSourcePicker({ boards, workspaceNames, hiddenBoardIds, onChange }: Props) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  // Presentation only. Absent = the default: open while there are few groups.
  const [opened, setOpened] = useState<Record<string, boolean>>({});
  const searchRef = useRef<HTMLInputElement>(null);
  const hidden = knownHidden(hiddenBoardIds, boards);
  const groups = groupSources(boards, workspaceNames, t('calendar.sharedBoards'));
  const shownGroups = searchSources(groups, query);
  const searching = query.trim() !== '';
  const shownCount = boards.length - hidden.length;
  const summary = useSourceSummary(boards.length, shownCount);
  const isOpen = (id: string) => searching || (opened[id] ?? groups.length <= 3);

  return (
    <section
      className={styles.boards}
      aria-label={t('calendar.sources.title')}
      data-testid="calendar-boards"
    >
      <div className={styles.boardsHead}>
        <h2 className={styles.boardsTitle}>{t('calendar.sources.heading')}</h2>
        <HelpTip topic={t('calendar.sources.title')} topicId="calendar-sources" />
        <span className={styles.summary} data-testid="calendar-sources-summary" role="status">
          {summary}
        </span>
        <Button
          size="sm"
          variant="ghost"
          disabled={hidden.length === 0}
          onClick={() => {
            onChange([]);
          }}
        >
          {t('calendar.showAll')}
        </Button>
        <Button
          size="sm"
          variant="ghost"
          disabled={hidden.length >= boards.length}
          onClick={() => {
            onChange(boards.map((board) => board.id));
          }}
        >
          {t('calendar.hideAll')}
        </Button>
      </div>
      <div className={styles.sourceSearch}>
        <input
          ref={searchRef}
          type="search"
          className={styles.search}
          aria-label={t('calendar.sources.search')}
          placeholder={t('calendar.sources.search')}
          value={query}
          maxLength={100}
          onChange={(event) => {
            setQuery(event.target.value);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Escape' && query) {
              event.stopPropagation(); // clears the search first; a second Escape closes the popup
              setQuery('');
            }
          }}
        />
        {searching && (
          <button
            type="button"
            className={styles.searchClear}
            aria-label={t('calendar.sources.clearSearch')}
            onClick={() => {
              setQuery('');
              searchRef.current?.focus(); // the button is about to disappear
            }}
          >
            <Icons.close />
          </button>
        )}
      </div>
      {shownGroups.length === 0 && (
        <p className={styles.sourceEmpty} data-testid="calendar-sources-empty">
          {t('calendar.sources.noMatch')}
        </p>
      )}
      {shownGroups.map((group) => {
        // The counts and the tri-state are about the WORKSPACE, not about the search result.
        const all = groups.find((candidate) => candidate.id === group.id)?.items ?? group.items;
        const on = all.filter((board) => !hidden.includes(board.id)).length;
        const open = isOpen(group.id);
        return (
          <div key={group.id} className={styles.workspaceGroup} data-testid="calendar-board-group">
            <div className={styles.groupHead}>
              <button
                type="button"
                className={styles.groupToggle}
                aria-expanded={open}
                aria-label={t(open ? 'calendar.sources.collapse' : 'calendar.sources.expand', {
                  name: group.name,
                })}
                disabled={searching}
                onClick={() => {
                  setOpened((current) => ({ ...current, [group.id]: !open }));
                }}
              >
                <span className={open ? styles.chevronOpen : styles.chevron} aria-hidden>
                  <Icons.chevronRight />
                </span>
              </button>
              <WorkspaceCheck
                name={group.name}
                on={on}
                total={all.length}
                onToggle={() => {
                  const ids = all.map((board) => board.id);
                  onChange(
                    on === all.length
                      ? [...new Set([...hidden, ...ids])]
                      : hidden.filter((id) => !ids.includes(id)),
                  );
                }}
              />
            </div>
            {open && (
              <ul className={styles.boardList}>
                {group.items.map((board) => {
                  const shown = !hidden.includes(board.id);
                  return (
                    <li key={board.id}>
                      <label className={`${styles.board} ${shown ? '' : styles.off}`}>
                        <input
                          type="checkbox"
                          className={styles.boardCheck}
                          checked={shown}
                          onChange={() => {
                            onChange(
                              shown
                                ? [...hidden, board.id]
                                : hidden.filter((id) => id !== board.id),
                            );
                          }}
                        />
                        <span
                          className={styles.boardDot}
                          style={{
                            background: shown ? boardColor(board) : 'transparent',
                            borderColor: boardColor(board),
                          }}
                          aria-hidden
                        />
                        <span className={styles.boardName}>{board.name}</span>
                      </label>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        );
      })}
    </section>
  );
}

/** The workspace's checkbox: checked = all boards on, mixed = some, empty = none. */
function WorkspaceCheck({
  name,
  on,
  total,
  onToggle,
}: {
  name: string;
  on: number;
  total: number;
  onToggle: () => void;
}) {
  const { t } = useTranslation();
  const ref = useRef<HTMLInputElement>(null);
  const mixed = on > 0 && on < total;
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = mixed;
  }, [mixed]);
  return (
    <label className={styles.workspaceHead}>
      <input
        ref={ref}
        type="checkbox"
        className={styles.workspaceCheck}
        checked={on === total}
        aria-checked={mixed ? 'mixed' : on === total}
        aria-label={t('calendar.workspaceToggle', { name })}
        onChange={onToggle}
      />
      <span className={styles.workspaceName} title={name}>
        {name}
      </span>
      <span className={styles.groupCount}>
        {on}/{total}
      </span>
    </label>
  );
}
